import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Mail, Phone, MapPin, Clock, ExternalLink, Instagram } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import SEO, { GMB_URL, INSTAGRAM_URL } from "@/components/SEO";
import { toast } from "sonner";
import { trackClick } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const onSubmit = async (e) => {
    e.preventDefault();
    trackClick("contact-form-submit");
    try {
      await axios.post(`${API}/leads`, {
        kind: "contact",
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      toast.success("Thank you — we'll respond within one business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (_) {
      toast.error("Could not submit — please try again or email us directly.");
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Sanatan Dharma"
        description="Reach the Sanatan Dharma team — email info@sanatandharmaonline.com or call +91-7982444690. We respond within one business day."
        keywords="contact sanatan dharma, sanatan dharma phone, sanatan dharma email, puja products enquiry"
        path="/contact"
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHeading overline="Reach Us" title="A calm conversation. With real people." subtitle="Whether you are a household, a retailer, or a distributor — we'd love to hear from you." />
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <div className="flex items-start gap-4" data-testid="contact-info-email">
              <Mail size={20} className="text-[#C89D3C] mt-1" />
              <div>
                <p className="overline mb-1">Email</p>
                <p className="font-serif-display text-xl text-[#3B2A1A]">info@sanatandharmaonline.com</p>
                <p className="text-sm text-[#8A7969] mt-1 font-light">For all general & retail enquiries.</p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-phone">
              <Phone size={20} className="text-[#C89D3C] mt-1" />
              <div>
                <p className="overline mb-1">Phone</p>
                <p className="font-serif-display text-xl text-[#3B2A1A]">+91-7982444690</p>
                <p className="text-sm text-[#8A7969] mt-1 font-light">Mon-Sat · 10 AM – 7 PM IST</p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-address">
              <MapPin size={20} className="text-[#C89D3C] mt-1" />
              <div>
                <p className="overline mb-1">Address</p>
                <p className="font-serif-display text-xl text-[#3B2A1A] leading-tight">
                  Sara Greenworks Pvt. Ltd.<br/>
                  Second Floor, Kh No. 26/3,<br/>
                  Main Seva Dhaam Road, Mandoli,<br/>
                  Delhi — 110093, India
                </p>
                <a
                  href={GMB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-gmb-link"
                  onClick={() => trackClick("contact-gmb-link")}
                  className="inline-flex items-center gap-1.5 mt-3 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#C89D3C] hover:text-[#3B2A1A] transition-colors"
                >
                  Get Directions on Google Maps <ExternalLink size={12} />
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-social">
              <Instagram size={20} className="text-[#C89D3C] mt-1" />
              <div>
                <p className="overline mb-1">Follow</p>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-link"
                  className="font-serif-display text-xl text-[#3B2A1A] hover:text-[#C89D3C] transition-colors"
                >
                  @sanatandharma_online
                </a>
                <p className="text-sm text-[#8A7969] mt-1 font-light">Daily rituals, launches & festival guides.</p>
              </div>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-info-hours">
              <Clock size={20} className="text-[#C89D3C] mt-1" />
              <div>
                <p className="overline mb-1">Business Hours</p>
                <p className="font-serif-display text-xl text-[#3B2A1A] leading-tight">Mon – Sat<br/><span className="text-base text-[#5C4A3A] font-light">10:00 AM – 7:00 PM IST</span></p>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white border border-[#3B2A1A]/10 p-8 lg:p-12 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { k: "name", l: "Your Name" },
                { k: "email", l: "Email", type: "email" },
              ].map((f) => (
                <div key={f.k}>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">{f.l}</label>
                  <input
                    required
                    type={f.type || "text"}
                    data-testid={`contact-input-${f.k}`}
                    value={form[f.k]}
                    onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                    className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 text-base text-[#3B2A1A] focus:outline-none focus:border-[#C89D3C]"
                  />
                </div>
              ))}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Subject</label>
              <input
                required
                data-testid="contact-input-subject"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 text-base text-[#3B2A1A] focus:outline-none focus:border-[#C89D3C]"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-[#8A7969]">Message</label>
              <textarea
                required
                rows={5}
                data-testid="contact-input-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-b border-[#3B2A1A]/20 py-3 text-base text-[#3B2A1A] focus:outline-none focus:border-[#C89D3C] resize-none"
              />
            </div>
            <button type="submit" className="btn-primary mt-2" data-testid="contact-submit-btn">
              Send Message <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
            <div>
              <p className="overline">Visit Us</p>
              <p className="font-serif-display text-3xl text-[#3B2A1A] mt-2 leading-tight">Our Delhi Office & Warehouse</p>
            </div>
            <a
              href={GMB_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-gmb-directions"
              onClick={() => trackClick("map-gmb-directions")}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#C89D3C] hover:text-[#3B2A1A] transition-colors"
            >
              Open on Google Maps <ExternalLink size={12} />
            </a>
          </div>
          <div className="aspect-[16/8] overflow-hidden border border-[#3B2A1A]/10" data-testid="contact-map">
            <iframe
              title="Sanatan Dharma Delhi Office"
              src="https://www.google.com/maps?q=Second+Floor+Kh+No+26%2F3+Main+Seva+Dhaam+Road+Mandoli+Delhi+110093&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
